import {
  AHRoutineIcon,
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { PropsWithChildren, useMemo, useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";

import { DateTime } from "luxon";

import * as d from "d3-scale";

import Svg, { Rect, Text } from "react-native-svg";

import { graphql, useFragment } from "react-relay";

import { formatValue } from "arkh/common";
import { RoutineIcon } from "./__generated__/KPIPerWeekdayAnalytics.graphql";
import { KPIWeeklyHighlightAnalytics$key } from "./__generated__/KPIWeeklyHighlightAnalytics.graphql";
import Color from "color";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";

export function KPIPerWeeklyHightlight(props: {
  kpi: KPIWeeklyHighlightAnalytics$key;
  sectionIdsFilter?: string[];
}) {
  const data = useFragment(
    graphql`
      fragment KPIWeeklyHighlightAnalytics on KPI {
        id
        title
        icon
        unit
        team {
          color
        }

        sectionAggregates {
          section {
            id
            title
            assignedTeam {
              icon
              color
              name
            }
          }
          results {
            date
            value
          }
        }
      }
    `,
    props.kpi
  );

  return (
    <WeeklyHightlight
      title={data.title}
      icon={data.icon}
      unit={data.unit}
      color={
        props.sectionIdsFilter && props.sectionIdsFilter.length === 1
          ? (data.sectionAggregates.find(
              (s) => s?.section.id === props.sectionIdsFilter![0]
            )?.section.assignedTeam?.color ??
            data.team?.color ??
            undefined)
          : (data.team?.color ?? undefined)
      }
      sections={(props.sectionIdsFilter
        ? data.sectionAggregates.filter(
            (s) =>
              s?.section.id && props.sectionIdsFilter!.includes(s.section.id)
          )
        : data.sectionAggregates
      ).map((s, i) => ({
        id: s?.section.id ?? i + "",
        color: s?.section.assignedTeam?.color ?? "#000",
        title: (s?.section.title || s?.section.assignedTeam?.name) ?? "",
        results:
          s?.results.map((r) => ({
            date: DateTime.fromISO(r.date),
            value: r.value,
          })) ?? [],
      }))}
    />
  );
}

function WeeklyHightlight({
  sections,
  title,
  icon,
  unit,
  color,
}: {
  color?: string;
  title?: string;
  icon?: RoutineIcon | null;
  unit: "CUSTOM" | "CURRENCY";
  sections: readonly {
    id: string;
    title: string;
    color: string;
    results: { readonly date: DateTime; readonly value: number }[];
  }[];
}) {
  const ul = useLayoutContext();
  const [width, setWidth] = useState(
    Dimensions.get("window").width - ul.space.medium * 2 - ul.space.xsmall * 2
  );

  const height = 150;

  const thisWeek = DateTime.local().minus({ days: 6 }).startOf("week");
  const lastWeek = thisWeek.minus({ week: 1 });

  const historyData = [thisWeek, lastWeek].map((weekDate) => {
    const results = sections.flatMap((s) => ({
      color: s.color,
      val: s.results.find((r) => r.date.equals(weekDate))?.value ?? 0,
    }));

    return {
      date: weekDate,
      results,
      total: results.reduce((a, b) => a + b.val, 0),
    };
  });

  const change = historyData[0].total / historyData[1].total;

  const max = Math.max(...historyData.map((l) => l.total));
  const [format, _u] = formatValue(unit, max);
  const u = _u ?? "";
  const paddingVertical = ul.space.medium;
  const paddingHorizontal = ul.space.medium;

  const barHeight = 30;
  const scaleY = d
    .scaleLinear()
    .domain([1, 0])
    .range([height - paddingVertical - barHeight, paddingVertical * 2]);

  const intl_tokens = useIntlTokens();

  const scaleX = useMemo(
    () =>
      d
        .scaleLinear()
        .range([width - paddingHorizontal, paddingHorizontal])
        .domain([max, 0]),

    [max, paddingHorizontal, width]
  );

  const theme = useTheme();

  const bgColor = Color(color ?? theme.primary);
  const frontColor =
    bgColor.contrast(Color(theme.lightText)) >
    bgColor.contrast(Color(theme.darkText))
      ? theme.lightText
      : theme.darkerText;
  if (historyData[0].total === 0 || historyData[1].total === 0) {
    return null;
  }

  const p = change < 1 ? 1 - change : change - 1;

  if (change === 1 || p > 3 || change < 0.2) {
    return null;
  }
  return (
    <Box spaceVertical="medium" spaceHorizontal="medium">
      <Box
        cornerRadius="medium"
        color="surface-primary"
        spaceVertical="medium"
        style={{ overflow: "hidden" }}
      >
        <HeaderContainer
          description={
            <HighlightDescription p={p} change={change} title={title ?? ""} />
          }
        >
          <AHText type="subtitle">
            <AHRoutineIcon size="xsmall" type={icon ?? "key"} /> {title ?? ""}
          </AHText>
          <AHText type="detail">
            <FormattedMessage
              description="KPIWeeklyHighlight Title"
              defaultMessage="Highlight"
            />
          </AHText>
        </HeaderContainer>

        <View
          style={{ flex: 1 }}
          onLayout={(e) => {
            setWidth(e.nativeEvent.layout.width);
          }}
        >
          <Svg
            style={{ flex: 1 }}
            pointerEvents="none"
            width={width}
            height={height}
          >
            {historyData.map(({ total, results, date }, i) => {
              const barWidth = scaleX(total) - scaleX(0);

              return (
                <React.Fragment key={date + ""}>
                  {barWidth < 2 ? null : (
                    <>
                      <Rect
                        width={barWidth}
                        height={barHeight}
                        x={scaleX(0)}
                        y={scaleY(i)}
                        rx={6}
                        stroke={theme["border-primary"]}
                        strokeWidth={StyleSheet.hairlineWidth}
                        fill={
                          i === 1 ? theme.background : (color ?? theme.primary)
                        }
                      />
                    </>
                  )}

                  <Text
                    dy={scaleY(i) - barHeight / 3}
                    x={scaleX(0) + 2}
                    fontFamily={AHTextFonts.BOLD as any}
                    fontSize={17}
                    fill={theme["text-primary"]}
                  >
                    {format(total)} {u ?? ""}
                  </Text>
                </React.Fragment>
              );
            })}

            {historyData.map(({ date }, i) => {
              return (
                <Text
                  key={i + ""}
                  dx={10}
                  dy={scaleY(i) + barHeight / 2 + 4}
                  x={paddingHorizontal}
                  fill={i === 1 ? theme.darkerText : frontColor}
                  fontFamily={AHTextFonts.BOLD as any}
                  fontSize={13}
                >
                  {intl_tokens.Week} {date.toFormat("W")}
                </Text>
              );
            })}
          </Svg>
        </View>
      </Box>
    </Box>
  );
}

function HeaderContainer(
  props: PropsWithChildren<{ description: React.ReactNode }>
) {
  return (
    <Box spaceHorizontal="medium">
      <Box
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {props.children}
      </Box>
      <Box spaceVertical="medium">
        <AHText type="stat-number" size={18} font={AHTextFonts.BOLD}>
          {props.description}
        </AHText>
      </Box>
      <AHSeparator />
    </Box>
  );
}

/*

  return `${title ?? ""} ${change < 1 ? "minskade" : "ökade"} med ${Math.abs(
    Math.floor(p * 100)
  )}% från veckan före`;

  */
function HighlightDescription(props: {
  change: number;
  p: number;
  title: string;
}) {
  const percent = Math.abs(Math.floor(props.p * 100));
  if (props.change > 1) {
    return (
      <FormattedMessage
        description="KPIWeeklyHighlightAnalytics description decrease"
        defaultMessage="{title} decreased {percent}% last week compared to the week before"
        values={{
          percent,
          title: props.title,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      description="KPIWeeklyHighlightAnalytics description inecrease"
      defaultMessage="{title} increased {percent}% last week compared to the week before"
      values={{
        percent,
        title: props.title,
      }}
    />
  );
}
